import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ExternalReviewer } from "../../api/assignments/external-review";
import {
	createExternalReview,
	createExternalReviewer,
	draftExternalReviewMessage,
	listExternalReviewers,
} from "../../api/assignments/external-review";
import {
	getPersonAssignments,
	type PersonAssignmentRetrieveType,
} from "../../api/assignments/person-assignment";
import { getVotes } from "../../api/vote";
import {
	type FunnelPersonSummary,
	PersonSelectionList,
	type SharedPerson,
	SinglePersonSection,
} from "./external-review-candidates";
import { type ReviewerRef, ReviewerSection } from "./external-reviewer-section";
import { isUpvoteGrade } from "./funnel-upvote-grades";

/** Opened from the Pipeline: the role is fixed and the candidates are picked
 * from its votes. Opened from a candidate's card: the candidate is fixed and
 * the role is picked from the boards they are on. From the Pipeline a picked
 * candidate opens in the page's own candidate panel (`onOpenPerson`), so this
 * dialog does not import that panel, which itself opens this dialog. */
type ExternalReviewDialogProps = {
	open: boolean;
	onClose: () => void;
	onCreated?: (reviewId: number) => void;
} & (
	| {
			assignmentId: number;
			onOpenPerson: (personId: number) => void;
			person?: undefined;
			personAssignmentId?: undefined;
	  }
	| {
			person: SharedPerson;
			personAssignmentId?: number;
			assignmentId?: undefined;
			onOpenPerson?: undefined;
	  }
);

export const ExternalReviewDialog = ({
	open,
	onClose,
	onCreated,
	assignmentId: fixedAssignmentId,
	onOpenPerson,
	person,
	personAssignmentId,
}: ExternalReviewDialogProps) => {
	// null while the person's boards load
	const [personRoles, setPersonRoles] = useState<
		PersonAssignmentRetrieveType[] | null
	>(null);
	const [pickedAssignmentId, setPickedAssignmentId] = useState<number | null>(null);
	const assignmentId = fixedAssignmentId ?? pickedAssignmentId;
	const [message, setMessage] = useState("");
	const [generating, setGenerating] = useState(false);
	const [generateError, setGenerateError] = useState<string | null>(null);
	const [reviewers, setReviewers] = useState<ExternalReviewer[]>([]);
	const [allPersons, setAllPersons] = useState<FunnelPersonSummary[]>([]);
	const [selectedPersonIds, setSelectedPersonIds] = useState<Set<number>>(new Set());
	const [expiresInDays, setExpiresInDays] = useState(14);
	const [submitting, setSubmitting] = useState(false);
	const [hasReviewer, setHasReviewer] = useState(false);

	// Reviewer state lives in ReviewerSection; we keep a ref to latest values
	const reviewerRef = useRef<ReviewerRef>({
		reviewer: null,
		isAddingNew: false,
		newName: "",
		newEmail: "",
	});

	const handleReviewerChange = useCallback((ref: ReviewerRef) => {
		reviewerRef.current = ref;
		const valid = ref.isAddingNew
			? Boolean(ref.newName && ref.newEmail)
			: Boolean(ref.reviewer);
		setHasReviewer(valid);
	}, []);

	const sharedPersonId = person?.id;

	useEffect(() => {
		if (!open) return;
		listExternalReviewers().then(setReviewers);
		setMessage("");
		setGenerateError(null);
		setExpiresInDays(14);
		setHasReviewer(false);
		reviewerRef.current = {
			reviewer: null,
			isAddingNew: false,
			newName: "",
			newEmail: "",
		};
		if (sharedPersonId !== undefined) {
			setSelectedPersonIds(new Set([sharedPersonId]));
			setPersonRoles(null);
			setPickedAssignmentId(null);
			getPersonAssignments({ personIds: [sharedPersonId] }).then((roles) => {
				const live = roles.filter((pa) => !pa.archived);
				setPersonRoles(live);
				// The board the card was opened from, else the only one there is.
				const preset =
					live.find((pa) => pa.id === personAssignmentId) ??
					(live.length === 1 ? live[0] : undefined);
				setPickedAssignmentId(preset?.assignment ?? null);
			});
		} else {
			getVotes({ assignment: fixedAssignmentId, limit: 500 }).then((res) => {
				// Deduplicate: one entry per person, upvote grade wins over downvote
				const personMap = new Map<number, FunnelPersonSummary>();
				for (const vote of res.results) {
					const existing = personMap.get(vote.person);
					const isUpvote = isUpvoteGrade(vote.grade);
					if (!existing) {
						personMap.set(vote.person, {
							id: vote.person,
							name: vote.person_name ?? `Person #${vote.person}`,
							headline: vote.person_headline ?? null,
							grade: vote.grade,
							is_upvote: isUpvote,
						});
					} else if (isUpvote && !existing.is_upvote) {
						existing.is_upvote = true;
						existing.grade = vote.grade;
					}
				}
				const persons = [...personMap.values()];
				setAllPersons(persons);
				setSelectedPersonIds(
					new Set(persons.filter((p) => p.is_upvote).map((p) => p.id))
				);
			});
		}
	}, [open, fixedAssignmentId, sharedPersonId, personAssignmentId]);

	const upvotedPersonIds = useMemo(
		() => allPersons.filter((p) => p.is_upvote).map((p) => p.id),
		[allPersons]
	);

	const handleSelectAll = useCallback(
		(upvotesOnly: boolean) => {
			if (upvotesOnly) {
				setSelectedPersonIds(new Set(upvotedPersonIds));
			} else {
				setSelectedPersonIds(new Set(allPersons.map((p) => p.id)));
			}
		},
		[upvotedPersonIds, allPersons]
	);

	const handleClearSelection = useCallback(() => {
		setSelectedPersonIds(new Set());
	}, []);

	const handleTogglePerson = useCallback((personId: number) => {
		setSelectedPersonIds((prev) => {
			const next = new Set(prev);
			if (next.has(personId)) {
				next.delete(personId);
			} else {
				next.add(personId);
			}
			return next;
		});
	}, []);

	const handleGenerate = async () => {
		if (assignmentId === null) return;
		const { reviewer, isAddingNew, newName } = reviewerRef.current;
		setGenerating(true);
		setGenerateError(null);
		try {
			setMessage(
				await draftExternalReviewMessage(
					assignmentId,
					[...selectedPersonIds],
					isAddingNew ? newName : (reviewer?.name ?? ""),
					message.trim() !== ""
				)
			);
		} catch {
			setGenerateError(
				"Could not generate a message. Write it yourself or try again."
			);
		} finally {
			setGenerating(false);
		}
	};

	const handleSubmit = useCallback(async () => {
		const {
			reviewer: selected,
			isAddingNew,
			newName,
			newEmail,
		} = reviewerRef.current;

		if (selectedPersonIds.size === 0 || assignmentId === null) return;
		if (!isAddingNew && !selected) return;
		if (isAddingNew && (!newName || !newEmail)) return;

		setSubmitting(true);

		let reviewer = selected;
		if (isAddingNew) {
			reviewer = await createExternalReviewer(newName, newEmail);
		}

		const created = await createExternalReview(
			assignmentId,
			reviewer.id,
			[...selectedPersonIds],
			message,
			expiresInDays
		);

		setSubmitting(false);
		onCreated?.(created.id);
		onClose();
	}, [assignmentId, selectedPersonIds, message, expiresInDays, onClose, onCreated]);

	return (
		<Dialog
			data-component="external-review-dialog"
			open={open}
			onClose={onClose}
			maxWidth="md"
			fullWidth
		>
			<DialogTitle data-component="dialog-title">Share for Review</DialogTitle>
			<DialogContent data-component="dialog-content">
				<ReviewerSection
					reviewers={reviewers}
					onReviewerChange={handleReviewerChange}
				/>

				{person ? (
					<SinglePersonSection
						person={person}
						roles={personRoles}
						assignmentId={pickedAssignmentId}
						onAssignmentChange={setPickedAssignmentId}
					/>
				) : (
					onOpenPerson && (
						/* Person selection — memoized to avoid re-render on text input */
						<PersonSelectionList
							allPersons={allPersons}
							selectedPersonIds={selectedPersonIds}
							onToggle={handleTogglePerson}
							onSelectAll={handleSelectAll}
							onClear={handleClearSelection}
							onOpenPerson={onOpenPerson}
						/>
					)
				)}

				<TextField
					data-component="review-message-field"
					label="Message to the reviewer (optional)"
					multiline
					minRows={3}
					fullWidth
					size="small"
					sx={{ mt: 2 }}
					value={message}
					disabled={generating}
					onChange={(e) => setMessage(e.target.value)}
				/>
				<Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
					<Button
						data-component="generate-review-message-button"
						size="small"
						variant="outlined"
						startIcon={
							generating ? (
								<CircularProgress size={14} color="inherit" />
							) : (
								<AutoAwesomeIcon />
							)
						}
						onClick={() => void handleGenerate()}
						disabled={
							generating ||
							assignmentId === null ||
							selectedPersonIds.size === 0
						}
					>
						{message.trim() ? "Regenerate" : "Generate"}
					</Button>
					{generateError && (
						<Typography
							variant="caption"
							color="error"
							data-component="generate-review-message-error"
						>
							{generateError}
						</Typography>
					)}
				</Stack>

				{/* Expiration */}
				<Box sx={{ mt: 2 }}>
					<TextField
						data-component="expiry-field"
						label="Link expires in (days)"
						type="number"
						size="small"
						sx={{ minWidth: 200 }}
						value={expiresInDays}
						onChange={(e) =>
							setExpiresInDays(
								Math.max(1, Math.min(90, Number(e.target.value)))
							)
						}
						slotProps={{
							htmlInput: { min: 1, max: 90 },
						}}
					/>
				</Box>
			</DialogContent>
			<DialogActions data-component="dialog-actions">
				<Button data-component="cancel-button" onClick={onClose}>
					Cancel
				</Button>
				<Button
					data-component="send-invite-button"
					variant="contained"
					onClick={handleSubmit}
					disabled={
						submitting ||
						selectedPersonIds.size === 0 ||
						assignmentId === null ||
						!hasReviewer
					}
				>
					{submitting ? "Sending..." : "Send Invite"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
